import React, { useMemo } from 'react';
import { useGraphData } from '../../hooks/useMeters';
import { buildWidgetConfig } from '../../services/meterApi';
import { financialYearAnchor } from '../../utils/financialYearWindow';
import {
  overnightBaseline,
  IDLE_FROM_HOUR,
  IDLE_TO_HOUR,
  NIGHTS,
} from '../../utils/overnightBaseline';
import MeterChart from '../details/MeterChart';
import { useInsights } from '../../../../../globalComponents/insights/useInsights';
import InsightsPanel from '../../../../../globalComponents/insights/InsightsPanel';
import { INSIGHTS_TAB } from './waterCharts';

/**
 * What still flows when nobody is drawing water.
 *
 * A leak hides inside a daily total — a few cubic metres against ninety is
 * nothing. It shows in the floor: the lowest hour of the night, which should
 * sit near zero on an idle site and does not when something is leaking, stuck
 * open, or drawing unmetered. The meters record this already.
 */

/**
 * Reads the rows out of the widget response, whichever shape it arrives in.
 * @param {object} payload - Response from the graph endpoint.
 * @returns {Array<object>} The rows, empty when there are none.
 */
const rowsOf = (payload) => {
  const rows = payload?.data?.data ?? payload?.data ?? [];
  return Array.isArray(rows) ? rows : [];
};

/** Keys this panel's insights, and names it in the summary across the page. */
const PANEL_ID = 'overnight-baseline';

/**
 * @param {string} currentFY - Financial year the nights are read from.
 * @param {string|number} locationId - Location filter from the page header.
 * @param {string|number} subLocationId - Sub-location filter from the page header.
 * @param {boolean} showInsights - Whether the page's insights toggle is on.
 * @param {Function} onInsightsReady - Told what this panel's narrative says, so
 *   the page can summarise every figure at once.
 */
const OvernightBaselinePanel = ({
  currentFY,
  locationId,
  subLocationId,
  showInsights = false,
  onInsightsReady,
}) => {
  const config = useMemo(() => {
    const anchor = financialYearAnchor(currentFY);
    if (!anchor) return null;

    const built = {
      financialYear: currentFY,
      startDateTime: anchor.clone().subtract(NIGHTS, 'days').startOf('day').toISOString(),
      endDateTime: anchor.toISOString(),
      locationId: locationId?.toString() || '1',
      title: 'Overnight baseline',
      module: 'Water',
      category: 'Withdrawal',
      subCategory: 'Ground Water',
      kpi: 'Borewell',
      kpiType: 'EXTERNAL',
      aggregationPeriodValue: 1,
      aggregationPeriodUnit: 'hours',
      xAxisField: 'displayPeriods',
    };

    if (subLocationId) built.subLocationId = subLocationId.toString();

    return buildWidgetConfig(built);
  }, [currentFY, locationId, subLocationId]);

  const { data, isLoading } = useGraphData(config, { enabled: !!config });

  const baseline = useMemo(() => overnightBaseline(rowsOf(data)), [data]);

  /** The floors, shaped as widget rows so the shared chart can draw them. */
  const chartRows = useMemo(
    () =>
      (baseline?.floors ?? []).map((entry) => ({
        period_ts: `${entry.night}T00:00:00`,
        displayPeriods: entry.night,
        sum_value: entry.floor,
      })),
    [baseline]
  );

  /**
   * The nightly floors, not the hourly readings they were distilled from.
   *
   * The request asks for every hour of eighty nights, and the panel plots one
   * figure per night. Handing over the response would have the narrative
   * describe a series nobody is looking at.
   */
  const graphData = useMemo(() => ({ data: chartRows }), [chartRows]);

  /**
   * Asked for even when there are too few nights to draw. The panel still
   * reports back, so a page-wide summary is not left waiting on a figure that
   * is never coming — and the request costs nothing, because an empty series
   * is answered before any model is asked.
   */
  const insightsEnabled = showInsights && !!config && !isLoading;

  const {
    insights,
    loading: insightsLoading,
    error: insightsError,
    refresh: refreshInsights,
  } = useInsights(
    INSIGHTS_TAB,
    PANEL_ID,
    graphData,
    config,
    insightsEnabled,
    onInsightsReady
  );

  if (isLoading) {
    return (
      <section className="ob-panel">
        <h3 className="section-title">Overnight Baseline</h3>
        <div className="wb-skeleton" aria-label="Loading overnight baseline" />
      </section>
    );
  }

  if (!baseline) {
    return (
      <section className="ob-panel">
        <h3 className="section-title">Overnight Baseline</h3>
        <div className="wb-card">
          <p className="wb-empty">
            Fewer than eight nights reported between {IDLE_FROM_HOUR}:00 and{' '}
            {IDLE_TO_HOUR}:00, which is too little to call a trend.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="ob-panel">
      <h3 className="section-title">Overnight Baseline</h3>

      <div className="wb-card">
        <div className="wb-head">
          <h4 className="wb-title">
            Lowest hour between {IDLE_FROM_HOUR}:00 and {IDLE_TO_HOUR}:00
          </h4>
          <span className={`wb-verdict wb-verdict--${baseline.rising ? 'bad' : 'ok'}`}>
            Floor {baseline.latest.toFixed(1)} m³/h
          </span>
          <p className="wb-why">
            The site is idle overnight, so whatever still flows is leakage, a
            stuck valve or an unmetered draw. A floor that keeps climbing is the
            earliest warning these meters can give.
          </p>
        </div>

        <div className="ob-chart">
          <MeterChart
            rows={chartRows}
            graphType="periodTotal"
            unit="m³/h"
            yAxisLabel="Overnight floor (m³/h)"
            seriesName="Overnight floor"
            aggregation="day"
            height={210}
          />
        </div>

        {baseline.rising ? (
          <p className="ob-verdict ob-verdict--bad">
            The floor has risen over the last {baseline.floors.length - 1} nights,
            from {baseline.earlierFloor.toFixed(1)} to {baseline.recentFloor.toFixed(1)} m³/h
            {baseline.risingNights > 1 && `, ${baseline.risingNights} of them consecutively`}.
            That is roughly {Math.round(baseline.weeklyLoss)} m³ a week leaving
            the site with nothing running.
          </p>
        ) : (
          <p className="ob-verdict">
            The floor is steady at about {baseline.recentFloor.toFixed(1)} m³/h,
            against {baseline.earlierFloor.toFixed(1)} over the nights before.
          </p>
        )}

        {showInsights && (
          <div className="im-insights-panel">
            <InsightsPanel
              insights={insights}
              loading={insightsLoading}
              error={insightsError}
              onRefresh={refreshInsights}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default OvernightBaselinePanel;
